import { useEffect, useMemo, useSyncExternalStore } from "react";
import { createAppInstallController } from "../lib/appInstall.mjs";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "./ui/dialog";

export function useAppInstall() {
  const controller = useMemo(() => createAppInstallController(
    typeof window === "undefined" ? undefined : window
  ), []);
  const state = useSyncExternalStore(controller.subscribe, controller.getSnapshot, controller.getSnapshot);
  useEffect(() => {
    controller.start();
    return () => controller.stop();
  }, [controller]);
  return { ...state, promptInstall: controller.promptInstall };
}

export default function InstallApp({ open, onOpenChange, onCloseAutoFocus, install }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onCloseAutoFocus={onCloseAutoFocus} className="max-h-[90dvh] overflow-y-auto bg-[var(--cc-navy)] text-white border-white/20">
        <DialogHeader>
          <DialogTitle>Install Competition Checker</DialogTitle>
          <DialogDescription>
            Add Competition Checker to your Android, iPhone or iPad home screen for easy access.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border border-white/15 bg-white/5 p-3 text-sm">
          <p className="text-white/80">Open this app link on your mobile:</p>
          <a
            href="https://competitionchecker.com/"
            className="inline-flex min-h-11 items-center break-all font-semibold text-[var(--cc-gold)] underline underline-offset-4"
          >
            competitionchecker.com
          </a>
          <p className="text-white/60">Use Chrome on Android, or Safari on iPhone and iPad.</p>
        </div>
        {install.standalone ? (
          <p role="status">Competition Checker is already running as an app.</p>
        ) : install.installed ? (
          <p role="status">Competition Checker is installed. Open it from your home screen.</p>
        ) : (
          <>
            {(install.canPrompt || install.prompting) && (
              <button
                type="button"
                className="rounded-md bg-[var(--cc-gold)] px-4 py-3 font-semibold text-[var(--cc-navy)] disabled:opacity-60"
                disabled={install.prompting}
                onClick={install.promptInstall}
              >
                {install.prompting ? "Opening browser prompt…" : "Install app"}
              </button>
            )}
            {install.message && <p role="status" className="text-sm text-white/80">{install.message}</p>}
            <section aria-labelledby="android-install-help" className="space-y-3 text-sm text-white/80">
              <h3 id="android-install-help" className="font-semibold text-white">Android: install with Chrome</h3>
              <ol className="list-decimal pl-5 space-y-2">
                <li>Open competitionchecker.com in Chrome or another Android browser that supports installation. If you are inside another app, open the page in your regular browser first.</li>
                <li>Open the browser menu and look for <strong>Install app</strong>, <strong>Install and create shortcut</strong> or <strong>Add to Home screen</strong>. If a submenu opens, choose <strong>Install</strong>.</li>
                <li>Follow the browser instructions, then open Competition Checker from your home screen.</li>
              </ol>
              <p>Menu names and availability vary by browser. Some browsers offer a shortcut instead of an installed app. If neither option appears, you can keep using the website.</p>
            </section>
            <section aria-labelledby="ios-install-help" className="space-y-3 border-t border-white/15 pt-4 text-sm text-white/80">
              <h3 id="ios-install-help" className="font-semibold text-white">iPhone and iPad: add with Safari</h3>
              <ol className="list-decimal pl-5 space-y-2">
                <li>Open <strong>competitionchecker.com</strong> in Safari. If it opened inside another app, copy the address and open it in Safari first.</li>
                <li>Tap <strong>Share</strong>. Depending on your Safari layout, open the page menu first to find Share. On iPad, you may also need to tap <strong>More</strong>.</li>
                <li>Choose <strong>Add to Home Screen</strong>. If it is missing, scroll down the Share actions and choose <strong>Edit Actions</strong> to add it.</li>
                <li>Keep <strong>Open as Web App</strong> switched on if shown, then tap <strong>Add</strong>. Open Competition Checker using its new home-screen icon.</li>
              </ol>
              <p>Safari uses these menu steps rather than the Android install button.</p>
            </section>
          </>
        )}
        <a href="/guides/install-competition-checker" className="inline-flex min-h-11 items-center text-sm text-[var(--cc-gold)] underline underline-offset-4">
          Read the full Android and iOS installation guide
        </a>
        <p className="text-sm text-white/60">
          Use an internet connection to load competitions and account details. Installing does not change your account or notification settings.
        </p>
      </DialogContent>
    </Dialog>
  );
}
